import type { SafetyPortfolioSiteCoverage } from '../../services/reportsApi';
import { formatOwnershipLabel, resolveSiteOwnershipMetadata } from './workspacePortfolioOwnership';

export type SiteComparisonSortKey = 'site' | 'region' | 'site_owner' | 'data_state' | 'workflows' | 'attention' | 'freshness';
export type SiteComparisonSortDirection = 'asc' | 'desc';

export interface SiteComparisonRow {
  siteId: string;
  displayName: string;
  regionLabel: string;
  siteOwner: string;
  dataState: string;
  dataStateLabel: string;
  stateDetail: string | null;
  included: boolean;
  workflowCount: number | null;
  attentionCount: number | null;
  freshnessAt: string | null;
  timezone: string | null;
  canOpenSite: boolean;
}

const DATA_STATE_LABELS: Record<string, string> = {
  loaded: 'Loaded',
  permission_denied: 'Permission denied',
  unavailable: 'Unavailable',
};

function normalize(value?: string | null): string {
  return String(value || '').trim();
}

function compareStrings(left: string, right: string): number {
  return left.localeCompare(right, undefined, { sensitivity: 'base' });
}

function compareNullableNumbers(
  left: number | null,
  right: number | null,
  direction: SiteComparisonSortDirection,
): number {
  if (left === null && right === null) return 0;
  if (left === null) return 1;
  if (right === null) return -1;
  return direction === 'asc' ? left - right : right - left;
}

export function formatPortfolioDataStateLabel(dataState?: string | null): string {
  const key = normalize(dataState).toLowerCase();
  return DATA_STATE_LABELS[key] || (key ? key.replace(/_/g, ' ') : 'Unknown');
}

export function formatPortfolioStateDetail(site: SafetyPortfolioSiteCoverage): string | null {
  const reason = normalize(site.reason);
  const detail = normalize(site.detail);
  if (reason && detail) return `${reason}: ${detail}`;
  return reason || detail || null;
}

export function buildSiteComparisonRows(
  siteCoverage: SafetyPortfolioSiteCoverage[] | undefined,
): SiteComparisonRow[] {
  return (siteCoverage || []).map((site) => {
    const siteId = normalize(site.site_id);
    const dataState = normalize(site.data_state).toLowerCase() || 'unknown';
    const included = Boolean(site.included) && dataState === 'loaded';
    const countsAvailable = included;
    const ownership = resolveSiteOwnershipMetadata(site);
    return {
      siteId,
      displayName: normalize(site.display_name) || siteId || 'Authorized site',
      regionLabel: formatOwnershipLabel(ownership.regionLabel),
      siteOwner: formatOwnershipLabel(ownership.siteOwner),
      dataState,
      dataStateLabel: formatPortfolioDataStateLabel(dataState),
      stateDetail: formatPortfolioStateDetail(site),
      included,
      workflowCount: countsAvailable ? Number(site.workflow_count ?? 0) : null,
      attentionCount: countsAvailable ? Number(site.attention_count ?? 0) : null,
      freshnessAt: countsAvailable ? (site.data_freshness_at || null) : null,
      timezone: site.timezone || null,
      canOpenSite: Boolean(siteId) && dataState === 'loaded',
    };
  });
}

export function sortSiteComparisonRows(
  rows: SiteComparisonRow[],
  sortKey: SiteComparisonSortKey,
  direction: SiteComparisonSortDirection = 'asc',
): SiteComparisonRow[] {
  const sorted = [...rows];
  sorted.sort((left, right) => {
    let result = 0;
    if (sortKey === 'site') {
      result = compareStrings(left.displayName, right.displayName);
    } else if (sortKey === 'region') {
      result = compareStrings(left.regionLabel, right.regionLabel);
    } else if (sortKey === 'site_owner') {
      result = compareStrings(left.siteOwner, right.siteOwner);
    } else if (sortKey === 'data_state') {
      result = compareStrings(left.dataStateLabel, right.dataStateLabel);
    } else if (sortKey === 'workflows') {
      result = compareNullableNumbers(left.workflowCount, right.workflowCount, direction);
    } else if (sortKey === 'attention') {
      result = compareNullableNumbers(left.attentionCount, right.attentionCount, direction);
    } else if (sortKey === 'freshness') {
      const leftFreshness = left.freshnessAt || '';
      const rightFreshness = right.freshnessAt || '';
      if (!leftFreshness && !rightFreshness) result = 0;
      else if (!leftFreshness) result = 1;
      else if (!rightFreshness) result = -1;
      else result = leftFreshness.localeCompare(rightFreshness);
    }
    if (sortKey !== 'workflows' && sortKey !== 'attention' && direction === 'desc') {
      result *= -1;
    }
    if (result === 0) result = compareStrings(left.displayName, right.displayName);
    return result;
  });
  return sorted;
}

export function portfolioSiteComparisonEmptyMessage(
  siteCoverage: SafetyPortfolioSiteCoverage[] | undefined,
): string | null {
  const sites = siteCoverage || [];
  if (sites.length === 0) {
    return 'No authorized sites were returned for comparison. Check organization scope and site access.';
  }
  const loaded = sites.filter((site) => normalize(site.data_state).toLowerCase() === 'loaded').length;
  if (loaded === 0) {
    return 'No sites loaded for comparison. Permission-denied or unavailable sites are listed with their data state.';
  }
  return null;
}
